import React from "react";
import yup from "yup";
import { observer } from "mobx-react";
import { toFinite } from "lodash";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography
} from "@material-ui/core";
import { regexps } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { Form, useFormStore } from "@modules/formx-react";
import { FormxDebugger, ToggleButtonGroup } from "@common/formx";
import RegularButton from "@components/buttons/RegularButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import { Text } from "@components/formx";
import { weekDays } from "@bookings/BookingProfile/TimeReport/components/utils";

export interface DayInfoData {
  lunch: string;
  miles: string;
  comment: string;
}

const schema = yup.object<DayInfoData>({
  lunch: yup.string().matches(regexps.onlyNumbersWithDecimals),
  miles: yup.string().matches(regexps.onlyNumbersWithDecimals),
  comment: yup.mixed() // yup.string()
});

type Props = {
  open: boolean;
  initialValues: DayInfoData | null;
  onClose: () => void;
  onSubmit: (values: DayInfoData) => void;
  onlyMiles?: boolean;

  isLunchTimeDisabled?: boolean;
  lunchTimeMax?: number;
  lunchTimeWarning?: string;
  lunchTimeHelperText?: string;
};

export const DayInfoForm = observer((props: Props) => {
  const form = useFormStore<DayInfoData>({
    initialValues: props.initialValues,
    schema,
    validate: (store) => {
      if (
        props.lunchTimeMax != null &&
        toFinite(store.values.lunch) > props.lunchTimeMax
      ) {
        return {
          lunch: `Ej tillåtet att rapportera rast utanför tillåtet intervall (07:00 - 19:00). Justera din rast-tid för detta intervall för att kunna spara.`
        };
      }
    },
    onSubmit: async () => {
      props.onSubmit(form.values);
    }
  });

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="xs" fullWidth>
      <FormxDebugger store={form} />
      <DialogTitle>{locale.Edit}</DialogTitle>

      <DialogContent style={{ overflow: "auto" }} data-test="DayInfoForm">
        <Form store={form} allowUpdates>
          <Grid container spacing={2}>
            {!props.onlyMiles && (
              <Grid item xs={12}>
                <Typography color="error" variant="caption">
                  {form.errors.lunch}
                </Typography>

                <Text
                  disabled={props.isLunchTimeDisabled}
                  label={locale.Lunch}
                  name="lunch"
                  TextFieldProps={{
                    type: "number",
                    hiddenLabel: true,
                    label: locale.Lunch,
                    inputProps: {
                      min: 0,
                      max: props.lunchTimeMax
                    }
                  }}
                  helperText={props.lunchTimeHelperText}
                />

                {props.lunchTimeWarning && (
                  <Typography variant="caption" color="primary">
                    {props.lunchTimeWarning}
                  </Typography>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <Text label={locale.Milage} name="miles" />
            </Grid>

            {!props.onlyMiles && (
              <>
                <Grid item xs={12}>
                  <Text label={locale.Comment} name="comment" />
                </Grid>

                <Grid item xs={12}>
                  <ToggleButtonGroup
                    name="weekDays"
                    exclusive={false}
                    wrap
                    label={locale.RepeatOnWeekDays}
                    options={weekDays.map((l, i) => ({
                      label: l,
                      value: i
                    }))}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Form>
      </DialogContent>

      <DialogActions>
        <RegularButton style={{ marginRight: "auto" }} onClick={props.onClose}>
          {locale.Cancel}
        </RegularButton>

        <PrimaryButton disabled={!form.canSubmit} onClick={() => form.submit()}>
          OK
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
});
